/* eslint-disable @angular-eslint/prefer-standalone */
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorResponse } from '@app/core/models/commom/error-response';
import { AppState } from '@app/state';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { InputStatus } from '@interfaces/a-organizar/inputStatus';
import { CreatePasswordRequest } from '@models/auth/create-password-request';
import {
  CompanyBasicInfoDto,
  CreatePasswordUserDataDto,
} from '@models/auth/create-password-user-data-dto';
import { TermsDocumentDto } from '@models/auth/terms-document-dto';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PasswordValidatorService } from '@services/helpers/password-validator.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { AuthenticationService } from '@services/http/authentication.service';
import { UserService } from '@services/http/user.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { selectLoadingMessage } from '@src/app/state/auth/auth.selectors';

@Component({
  selector: 'app-create-password-form',
  templateUrl: './create-password-form.component.html',
  styleUrls: ['./create-password-form.component.scss'],
  standalone: false,
})
export class CreatePasswordFormComponent extends VaultBaseComponent implements OnInit {
  public createPasswordProps = nameof<CreatePasswordRequest>();
  showPasswordVerification: boolean = false;

  loadingMessage: string | undefined;
  isLoadingUserData: boolean = false;
  companyInfo: CompanyBasicInfoDto | null = null;

  /**
   * Termos que esta pessoa precisa aceitar (card 2916).
   *
   * Vem do backend junto com os dados do usuário: é ele que sabe se ela é parceiro — que
   * tem termos próprios — e é a `version` dele que é gravada no aceite.
   */
  terms: TermsDocumentDto | null = null;

  /**
   * Rótulo do checkbox de aceite, no formato que o `app-checkbox` espera.
   *
   * Trocado em `fetchUserData` quando os termos chegam: o parceiro aceita o Acordo de
   * Confidencialidade (NDA), não "termos de uso", e o rótulo tem de nomear o documento e a
   * versão que ficam gravados no aceite (card 3407), como já faz a landing de cadastro.
   */
  acceptTermsCheckbox: Checkbox = {
    label: 'Li e aceito os termos de uso',
    name: 'acceptTerms',
    value: 'acceptTerms',
    isDisabled: false,
  };

  createPasswordForm: FormGroup = this.formBuilder.group(
    {
      [this.createPasswordProps.firstName]: ['', [Validators.required]],
      [this.createPasswordProps.surName]: ['', [Validators.required]],
      [this.createPasswordProps.phoneNumber]: ['55'],
      email: [''],
      [this.createPasswordProps.password]: [
        '',
        [
          Validators.required,
          this.passwordValidatorService.minimumLength(8),
          this.passwordValidatorService.containsUppercase(),
          this.passwordValidatorService.containsNumber(),
          this.passwordValidatorService.containsSymbol(),
        ],
      ],
      [this.createPasswordProps.confirmPassword]: ['', Validators.required],
      [this.createPasswordProps.token]: ['', Validators.required],
      [this.createPasswordProps.userId]: ['', Validators.required],
      // Sem validador aqui, e não por esquecimento: ele é ligado em `fetchUserData`, só
      // quando o backend devolve termos. Se ficasse fixo, uma resposta sem `terms` (front
      // novo contra backend antigo, ou a consulta falhando — o handler dela apenas loga)
      // deixaria o formulário inválido SEM nenhum campo na tela para consertar, e o botão
      // de concluir travado sem explicação.
      acceptTerms: [null],
    },
    { validators: this.confirmPasswordValidator() }
  );

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private utilsService: UtilsService,
    private passwordValidatorService: PasswordValidatorService,
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private toast: ToastHelperService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private formErrorHandler: FormErrorHandlerService
  ) {
    super(translateService, store);
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      if (params[this.createPasswordProps.token] && params[this.createPasswordProps.userId]) {
        const token = params[this.createPasswordProps.token];
        const userId = params[this.createPasswordProps.userId];

        this.createPasswordForm.patchValue({
          token: token,
          userId: userId,
        });

        // Fetch user data
        this.fetchUserData(userId, token);
      }
    });

    if (this.store)
      this.store.select(selectLoadingMessage).subscribe(loading => {
        if (loading) {
          this.loadingMessage = loading;
        }
      });
  }

  private fetchUserData(userId: string, token: string): void {
    this.isLoadingUserData = true;
    this.userService.getCreatePasswordUserData(userId, token).subscribe({
      next: (userData: CreatePasswordUserDataDto | null) => {
        this.isLoadingUserData = false;
        if (userData) {
          this.createPasswordForm.patchValue({
            firstName: userData.firstName || '',
            surName: userData.surName || '',
            phoneNumber: userData.phoneNumber || '55',
            email: userData.email || '',
          });
          // Store company info if available
          this.companyInfo = userData.company || null;
          this.terms = userData.terms || null;
          if (this.terms) {
            // Objeto novo, e não mutação: o `app-checkbox` só redesenha quando a referência muda.
            this.acceptTermsCheckbox = {
              ...this.acceptTermsCheckbox,
              label: `Li e aceito: ${this.terms.title} v${this.terms.version}`,
            };
          }
          this.aplicarValidacaoDosTermos();
        }
      },
      error: error => {
        this.isLoadingUserData = false;
        console.error('Error fetching user data:', error);
        // Don't show error toast - fields will just be empty
      },
    });
  }

  /**
   * Liga o aceite dos termos ao formulário — só quando há termos para aceitar.
   *
   * ⚠️ O validador é `required`, e **não** `requiredTrue`, porque o `app-checkbox` não é um
   * `ControlValueAccessor`: ele grava no control o `value` do objeto `Checkbox` (a string
   * `'acceptTerms'`) quando marcado, e `null` quando desmarcado — nunca `true`. Com
   * `requiredTrue` o control seria inválido **mesmo com o checkbox marcado**, e o botão de
   * concluir ficaria travado para todo usuário da plataforma, não só para o parceiro.
   *
   * E a validação é condicional porque o bloco de termos só é renderizado quando eles
   * existem: um validador fixo deixaria o formulário inválido, sem campo na tela para
   * consertar, caso a consulta dos dados falhe ou o backend não devolva `terms`.
   */
  private aplicarValidacaoDosTermos(): void {
    const control = this.createPasswordForm.get('acceptTerms');
    if (!control) return;

    control.setValidators(this.terms ? [Validators.required] : []);
    control.updateValueAndValidity();
  }

  confirmPasswordValidator(): ValidationErrors | null {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const password = formGroup.get(this.createPasswordProps.password)?.value;
      const confirmPassword = formGroup.get(this.createPasswordProps.confirmPassword)?.value;
      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  onSubmit() {
    this.createPassword();
  }

  async createPassword() {
    if (this.createPasswordForm.invalid) {
      this.createPasswordForm.markAllAsTouched();
      return;
    }
    const pathToRedirect = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

    this.utilsService.setIsLoading(true);
    const formData = this.createPasswordForm.getRawValue();

    // Handle phone number default value - if unchanged from '55', send null
    if (formData.phoneNumber === '55') {
      formData.phoneNumber = null;
    }

    // Vai a VERSÃO que esta tela exibiu, não um "aceitei": o backend confere se ela ainda é
    // a corrente e recusa se os termos tiverem mudado enquanto a aba estava aberta.
    formData.acceptedTermsVersion = this.terms?.version;

    this.userService.postCreatePassword(formData).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        this.toast.showToastSuccess(this.t(this.tk.Auth.PasswordCreatedSuccessfully), '');
        if (response) {
          this.authenticationService.performLogin(
            null,
            this.createPasswordForm,
            pathToRedirect,
            response
          );
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        if (error instanceof ErrorResponse) {
          this.formErrorHandler.applyFormErrors(this.createPasswordForm, error);
        } else {
          console.log('error: ', error);
        }
        this.toast.showToastError(
          this.t(this.tk.Auth.ErrorCreatingPassword),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  hasError(controlName: string, errorName: string): boolean {
    return this.createPasswordForm.controls[controlName].hasError(errorName);
  }

  handleInputStatus(inputStatus: InputStatus) {
    this.showPasswordVerification = inputStatus.dirty;
  }

  get firstName() {
    return this.createPasswordForm.get(this.createPasswordProps.firstName);
  }

  get surName() {
    return this.createPasswordForm.get(this.createPasswordProps.surName);
  }

  get phoneNumber() {
    return this.createPasswordForm.get(this.createPasswordProps.phoneNumber);
  }

  get password() {
    return this.createPasswordForm.get(this.createPasswordProps.password);
  }

  get confirmPassword() {
    return this.createPasswordForm.get(this.createPasswordProps.confirmPassword);
  }

  get email() {
    return this.createPasswordForm.get('email');
  }
}
